import Link from "next/link";
import type { ReactNode } from "react";

import { HomeBoardBackground } from "@/components/student/home-board-background";

const POINTS = [
  "Daily 20 practice matched to your level",
  "Real Edexcel and AQA exam questions",
  "An AI tutor that helps you fix mistakes",
];

function Logo({ onDark = false }: { onDark?: boolean }) {
  return (
    <Link href="/login" className="inline-flex items-center gap-2.5">
      <span
        className={
          onDark
            ? "flex size-9 items-center justify-center rounded-xl bg-white/95 text-sm font-bold text-brand-700 shadow-sm"
            : "flex size-9 items-center justify-center rounded-xl bg-brand-600 text-sm font-bold text-white shadow-sm"
        }
        aria-hidden
      >
        15
      </span>
      <span
        className={
          onDark
            ? "text-lg font-semibold tracking-tight text-white"
            : "text-lg font-semibold tracking-tight text-ink-950"
        }
      >
        Daily15
      </span>
    </Link>
  );
}

/** Split layout for sign-in / sign-up: brand panel on wide screens, form card beside it. */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-full bg-ink-50 font-sans">
      <aside className="relative hidden w-[44%] max-w-xl overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12">
        <HomeBoardBackground />
        <div className="relative">
          <Logo onDark />
        </div>
        <div className="relative max-w-sm">
          <h2 className="text-3xl leading-tight font-semibold tracking-tight text-balance text-white">
            GCSE Maths practice that adapts to you.
          </h2>
          <ul className="mt-8 space-y-4">
            {POINTS.map((point) => (
              <li key={point} className="flex items-start gap-3 text-base text-white/85">
                <span
                  className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-white/15 text-xs text-white"
                  aria-hidden
                >
                  ✓
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-sm text-white/65">
          For students and teachers · Higher and Foundation tier
        </p>
      </aside>

      <main className="flex flex-1 flex-col items-center justify-center px-5 py-10 sm:px-8">
        <div className="mb-8 lg:hidden">
          <Logo />
        </div>
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-ink-200 bg-white p-7 shadow-sm sm:p-9">
            <h1 className="text-2xl font-semibold tracking-tight text-ink-950">{title}</h1>
            <p className="mt-1.5 text-sm text-ink-600">{subtitle}</p>
            <div className="mt-7">{children}</div>
          </div>
          {footer && <div className="mt-6 space-y-2 text-center text-sm text-ink-600">{footer}</div>}
        </div>
      </main>
    </div>
  );
}
